import * as React from 'react';
import * as classnames from 'classnames';
import App from '@nexxt/bot-client/dist/bot-preview';
import { ProjectInfo, QTYPES } from '@nexxt/common/types';
import { devices } from '../../../mapping/overview';
import {
    UilRedo,
    UilDesktop,
    UilTablet,
    UilMobileAndroid,
    UilExternalLinkAlt
} from '@iconscout/react-unicons';
import { getLastAfterSplit } from '@nexxt/common/services/TextService';

const BotPreview: React.FC<{
    selectedDevices: string[];
    previewPackageId: number;
    projectInfo?: ProjectInfo;
    waiting?: boolean; // show pageloader while this is true
    accountURL?: string;
    questionType?: string;
    quotaGroup?: string;
    updateQuotaNumber?: (value) => void;
    isBroken: boolean;
    previewLink?: string;
}> = (props) => {
    const {
        selectedDevices,
        previewPackageId,
        projectInfo,
        questionType,
        quotaGroup,
        isBroken,
        previewLink
    } = props;

    const [activeDevice, setActiveDevice] = React.useState('browser');
    const [botInstance, setBotInstance] = React.useState(0);
    const [restartOverlayActive, setRestartOverlayActive] =
        React.useState(false);
    const pageTitle = projectInfo?.pageTitle || previewPackageId;
    const remountBot = () => {
        // remount bot simply by changing key

        setBotInstance(botInstance + 1);
    };
    const restartPreview = () => {
        remountBot();
        props.updateQuotaNumber(null);
        setRestartOverlayActive(false);
    };

    React.useEffect(() => {
        remountBot();
    }, [activeDevice]);
    const addQuotaGroup = (value) => {
        props.updateQuotaNumber(value);
        remountBot();
        setRestartOverlayActive(false);
    };

    const renderDeviceIcon = (value) => {
        switch (value) {
            case 'mobile':
                return <UilMobileAndroid icon={20} />;
            case 'tablet':
                return <UilTablet icon={20} />;
            default:
                return <UilDesktop icon={20} />;
        }
    };
    const quotaGroupEnabled =
        !!projectInfo.predefinedAudience?.length &&
        !quotaGroup &&
        !questionType;
    const waiting = restartOverlayActive || quotaGroupEnabled;
    const showOverlay = isBroken || restartOverlayActive || quotaGroupEnabled;

    return (
        <React.Fragment>
            <div className="preview-container">
                <div id="bot-preview" className={`bot-preview ${activeDevice}`}>
                    <div className="bot-preview--title-bar">
                        <div className="bot-preview--buttons">
                            <div className="bot-preview--circle bot-preview--circle--red"></div>
                            <div className="bot-preview--circle bot-preview--circle--orange"></div>
                            <div className="bot-preview--circle bot-preview--circle--green"></div>
                        </div>
                        <div className="bot-preview--page-title">
                            {pageTitle}
                        </div>
                    </div>
                    <div className="bot-preview--body">
                        <div
                            className={`bot-app ${
                                showOverlay && 'has-overlay'
                            }`}
                        >
                            {restartOverlayActive && ( // BOT PREVIEW OVERLAY
                                <div
                                    className={`bot-preview-overlay ${activeDevice}`}
                                >
                                    <h3>{`You've completed the preview! 🎉`}</h3>
                                    <button
                                        className="bot-preview-overlay-button btn btn--md btn--lt-gray"
                                        onClick={restartPreview}
                                    >
                                        <UilRedo size={20} />
                                        <span>Preview Again</span>
                                    </button>
                                </div>
                            )}
                            {quotaGroupEnabled && (
                                <div
                                    className={`bot-preview-overlay ${activeDevice}`}
                                >
                                    <h3>Please select audience group</h3>
                                    {projectInfo.predefinedAudience.map(
                                        (audience) => {
                                            const groupNumber =
                                                getLastAfterSplit(
                                                    audience.groupValue,
                                                    '/'
                                                );
                                            return (
                                                <button
                                                    key={audience.groupValue}
                                                    className="bot-preview-overlay-button btn btn--md btn--lt-gray m-lr-5"
                                                    onClick={() =>
                                                        addQuotaGroup(
                                                            groupNumber
                                                        )
                                                    }
                                                >
                                                    {audience.label +
                                                        (audience?.subLabel ||
                                                            '')}
                                                </button>
                                            );
                                        }
                                    )}
                                </div>
                            )}
                            {isBroken &&
                                previewLink && ( // PREVIEW BROKEN OVERLAY
                                    <div
                                        className={`bot-preview-overlay ${activeDevice}`}
                                    >
                                        <h3>
                                            Click to preview this question in a
                                            new tab! 🚀
                                        </h3>
                                        <a
                                            className="bot-preview-overlay-button btn btn--md btn--lt-gray"
                                            href={previewLink}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <UilExternalLinkAlt size={20} />
                                            <span>Show Preview</span>
                                        </a>
                                    </div>
                                )}{' '}
                            <div id="bot-container">
                                {!waiting && botInstance ? (
                                    <App
                                        key={botInstance}
                                        package={previewPackageId}
                                        projectInfo={projectInfo}
                                        disableSession
                                        isEmbedded
                                        testMode
                                        quotaGroup={quotaGroup}
                                        waiting={waiting}
                                        handleTerminate={() =>
                                            setTimeout(() => {
                                                setRestartOverlayActive(true);
                                            }, 2000)
                                        }
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="bot-preview--footer">
                        {devices.map((d, i) => {
                            const active = selectedDevices?.includes(d.value)
                                ? 'active'
                                : '';
                            return (
                                <span
                                    key={i}
                                    className={`preview--device ${active}`}
                                    onClick={(e) =>
                                        !!active && setActiveDevice(d.value)
                                    }
                                >
                                    {renderDeviceIcon(d.value)}
                                </span>
                            );
                        })}
                    </div>
                </div>
                {previewLink && (
                    <div className="row">
                        <div className="bot-preview--footer">
                            <span>
                                <b>Note:</b> For most accurate representation of
                                the user experience please open preview on a{' '}
                                <a
                                    href={previewLink}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    new tab
                                </a>
                            </span>
                            .
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

export default BotPreview;
